import { useEffect, useState } from 'react';
import {
  buildQueryFilters,
  buildQuerySortingFromStateArray,
  mapColumnFiltersToCrudFilters,
  TableName,
  VectorContractDto,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { Button, SibDataTable, TableWrapper, useTableSettings } from '@sib/ui';
import { NoVendors } from '@sib/ui/icons';
import { NoTableData } from '@spendbrain/common';
import { useDialogStore } from '@spendbrain/common/stores';
import { TableId } from '@spendbrain/config/table-ids';
import { useTablePreferencesSettings } from '@spendbrain/hooks';
import { useAppTranslation } from '@spendbrain/modules/vendors/hooks';
import { ContractPreview } from './ContractPreview';
import {
  ContractPreview as ContractPreviewType,
  useContracts,
} from './ContractsTable.utils';
import { useContractsColumns } from './ContractsTable.utils';

export interface ContractsTableProps {
  vendorId: string;
}

export const ContractsTable = ({ vendorId }: ContractsTableProps) => {
  const { t } = useAppTranslation();
  const { close } = useDialogStore();

  const { onColumnVisibilityChange, state, disabledColumns } =
    useTablePreferencesSettings(TableName.Contracts);
  const [contractPreview, setContractPreview] =
    useState<ContractPreviewType | null>(null);
  const columns = useContractsColumns({ setContractPreview });
  const organizationId = useUserSetupStore(state => state.organization.id);

  const tableSettings = useTableSettings<VectorContractDto>({
    tableId: TableId.Contracts,
    sorting: true,
    ...state,
    onColumnVisibilityChange,
    disabledColumns,
    defaultColumnPinningState: {
      left: ['id'],
      right: ['actions'],
    },
  });
  const { pagination, columnFilters, sorting, resetPagination } = tableSettings;

  useEffect(() => resetPagination(), [organizationId, resetPagination]);

  const mappedColumnFilters = mapColumnFiltersToCrudFilters(
    columnFilters,
    columns
  );
  const { data, isLoading, rowCount } = useContracts({
    organizationId,
    vendorId,
    page: pagination.pageIndex,
    size: pagination.pageSize,
    sort: buildQuerySortingFromStateArray(sorting),
    filter: buildQueryFilters(mappedColumnFilters),
  });

  return (
    <>
      {!contractPreview ? (
        <TableWrapper className="flex-col items-center gap-2">
          <SibDataTable
            TablePaginationProps={{
              itemsPerPageLabel: t('components.tablePagination.itemsPerPage'),
            }}
            loading={isLoading}
            columns={columns}
            rowCount={rowCount}
            data={data}
            settings={tableSettings}
            preserveSelectedValues
            TableContainerProps={{
              className: 'flex-[1_1] flex flex-col items-stretch',
            }}
            noResultsPlaceholder={
              tableSettings.hasFiltersSet ? (
                <NoTableData
                  title={t('noFilteredResults.title')}
                  description={t('noFilteredResults.description')}
                  className="py-10"
                />
              ) : (
                <NoTableData
                  title={t('components.contracts.noData.title')}
                  description={t('components.contracts.noData.description')}
                  className="py-10"
                  Icon={NoVendors}
                />
              )
            }
          />
        </TableWrapper>
      ) : (
        <ContractPreview
          fileUrl={contractPreview?.fileUrl || ''}
          title={contractPreview?.title || ''}
        />
      )}
      <Button
        onClick={() => (contractPreview ? setContractPreview(null) : close())}
        variant="outline"
        className="mx-auto mt-2 w-16"
      >
        {contractPreview ? t('back') : t('close')}
      </Button>
    </>
  );
};
